import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Plus, Search, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';
import { companiesApi, usersApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingPanels,
  PageHeader,
  PageShell,
  ScrollHint,
  TextField,
  apiMessage,
  describedBy,
  fieldControl,
  glassPanel,
  glassStrong,
  glassSubtle,
  pillButton,
} from '@/pages/glass';

const ROLES = [
  { value: 'vdrive_super_admin', label: 'Vdrive super admin' },
  { value: 'vdrive_admin', label: 'Vdrive admin' },
  { value: 'company_admin', label: 'Company admin' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'installer', label: 'Installer' },
  { value: 'viewer', label: 'Viewer' },
];

const COMPANY_TYPES = [
  { value: 'partner', label: 'Partner' },
  { value: 'vdrive', label: 'Vdrive' },
];

const PERMISSIONS = [
  'companies.view',
  'users.view',
  'users.manage',
  'requests.view',
  'requests.create',
  'requests.edit',
  'requests.submit',
  'requests.review',
  'requests.comment',
  'requests.install',
  'chargers.view',
  'chargers.manage',
  'operations.view',
  'operations.manage',
  'tariffs.manage',
];

const ROLE_BASELINE = {
  vdrive_super_admin: ['*'],
  vdrive_admin: [
    'companies.view', 'users.view', 'users.manage', 'requests.view', 'requests.create',
    'requests.edit', 'requests.submit', 'requests.comment', 'requests.install',
    'chargers.view', 'chargers.manage', 'operations.view', 'operations.manage', 'tariffs.manage',
  ],
  company_admin: [
    'users.view', 'users.manage', 'requests.view', 'requests.review', 'requests.comment',
    'requests.install', 'chargers.view', 'operations.view', 'operations.manage',
  ],
  inspector: ['requests.view', 'requests.review', 'requests.comment', 'chargers.view'],
  installer: ['requests.view', 'requests.comment', 'requests.install', 'chargers.view', 'operations.view'],
  viewer: ['requests.view', 'chargers.view', 'operations.view'],
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_PATTERN = /^[A-Za-z0-9_-]{2,16}$/;

const emptyCompany = { name: '', code: '', company_type: 'partner' };
const emptyUser = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'viewer',
  company_id: '',
  permission_grants: [],
  permission_revokes: [],
};

const LEGACY_ROLES = { admin: 'vdrive_super_admin', operator: 'company_admin' };
const normalizeRole = (role) => LEGACY_ROLES[role] || role || 'viewer';
const roleLabel = (role) => {
  const normalized = normalizeRole(role);
  return ROLES.find((item) => item.value === normalized)?.label || String(normalized).replaceAll('_', ' ');
};
const isWildcard = (role) => (ROLE_BASELINE[role] || []).includes('*');
const concreteBaseline = (role) => (ROLE_BASELINE[role] || []).filter((permission) => permission !== '*');

const reconcilePermissions = (role, grants = [], revokes = []) => {
  if (isWildcard(role)) return { permission_grants: grants, permission_revokes: revokes };
  const baseline = new Set(concreteBaseline(role));
  return {
    permission_grants: grants.filter((permission) => !PERMISSIONS.includes(permission) || !baseline.has(permission)),
    permission_revokes: revokes.filter((permission) => !PERMISSIONS.includes(permission) || baseline.has(permission)),
  };
};

const effectivePermissions = (role, grants = [], revokes = []) => {
  if (isWildcard(role)) return ['*'];
  const access = new Set(concreteBaseline(role));
  grants.forEach((permission) => access.add(permission));
  revokes.forEach((permission) => access.delete(permission));
  return [...access].sort();
};

const togglePermission = (list, permission, checked) => (
  checked ? [...new Set([...list, permission])] : list.filter((item) => item !== permission)
);

const validateCompany = (company, companies) => {
  const errors = {};
  const name = company.name.trim();
  const code = company.code.trim();
  if (name.length < 2) errors.name = 'Enter a company name with at least 2 characters.';
  if (!CODE_PATTERN.test(code)) errors.code = 'Use 2–16 letters, numbers, hyphens, or underscores.';
  else if (companies.some((item) => item.code?.toLowerCase() === code.toLowerCase())) {
    errors.code = 'A company with this code already exists.';
  }
  if (!COMPANY_TYPES.some((item) => item.value === company.company_type)) errors.company_type = 'Choose a company type.';
  return errors;
};

const validateUser = (user, { editing = false, users = [], currentId } = {}) => {
  const errors = {};
  if (user.name.trim().length < 2) errors.name = 'Enter a name with at least 2 characters.';
  const email = user.email.trim();
  if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  else if (users.some((item) => item.id !== currentId && item.email?.toLowerCase() === email.toLowerCase())) {
    errors.email = 'A user with this email already exists.';
  }
  if (!editing) {
    if ((user.password || '').length < 8) errors.password = 'Use at least 8 characters.';
    if (user.password !== user.confirmPassword) errors.confirmPassword = 'Passwords do not match.';
  } else if (user.password || user.confirmPassword) {
    if ((user.password || '').length < 8) errors.password = 'Use at least 8 characters, or leave both fields blank.';
    if (user.password !== user.confirmPassword) errors.confirmPassword = 'Passwords do not match.';
  }
  if (!ROLES.some((item) => item.value === user.role)) errors.role = 'Choose a role.';
  const overlap = (user.permission_grants || []).filter((permission) => (user.permission_revokes || []).includes(permission));
  if (overlap.length) errors.permissions = `Remove ${overlap.join(', ')} from either grants or revokes.`;
  return errors;
};

const PermissionChecklist = ({ legend, hint, options, selected, onToggle, empty }) => (
  <fieldset className="space-y-2">
    <legend className="text-sm font-medium">{legend}</legend>
    <p className="text-xs text-muted-foreground">{hint}</p>
    {options.length === 0 ? (
      <p className={cn(glassSubtle, 'px-3 py-2 text-sm text-muted-foreground')}>{empty}</p>
    ) : (
      <div className="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
        {options.map((permission) => {
          const checked = selected.includes(permission);
          const inputId = `${legend}-${permission}`.replace(/[^a-z0-9_-]+/gi, '-');
          return (
            <label key={permission} className={cn(glassSubtle, 'flex cursor-pointer items-center gap-2 px-3 py-2')}>
              <Checkbox
                id={inputId}
                checked={checked}
                onCheckedChange={(value) => onToggle(permission, value === true)}
                aria-label={`${checked ? 'Remove' : 'Add'} ${permission}`}
              />
              <span className="font-mono text-xs">{permission}</span>
            </label>
          );
        })}
      </div>
    )}
  </fieldset>
);

const PermissionEditor = ({ role, grants, revokes, onChange, error }) => {
  if (isWildcard(role)) {
    return (
      <p className={cn(glassSubtle, 'px-3 py-3 text-sm text-muted-foreground')}>
        Super admin includes every permission. Individual grants and revokes are stored, but the wildcard still allows full access.
      </p>
    );
  }

  const baseline = new Set(concreteBaseline(role));
  const grantOptions = [
    ...PERMISSIONS.filter((permission) => !baseline.has(permission)),
    ...grants.filter((permission) => !PERMISSIONS.includes(permission)),
  ];
  const revokeOptions = [
    ...concreteBaseline(role),
    ...revokes.filter((permission) => !PERMISSIONS.includes(permission)),
  ];
  const effective = effectivePermissions(role, grants, revokes);

  return (
    <div className="space-y-4">
      <PermissionChecklist
        legend="Grant extra permissions"
        hint="Add access this role does not include."
        options={grantOptions}
        selected={grants}
        empty="This role already includes every standard permission."
        onToggle={(permission, checked) => onChange({
          permission_grants: togglePermission(grants, permission, checked),
          permission_revokes: revokes.filter((item) => item !== permission),
        })}
      />
      <PermissionChecklist
        legend="Revoke role permissions"
        hint="Remove access this role normally includes."
        options={revokeOptions}
        selected={revokes}
        empty="This role has no standard permissions to revoke."
        onToggle={(permission, checked) => onChange({
          permission_grants: grants.filter((item) => item !== permission),
          permission_revokes: togglePermission(revokes, permission, checked),
        })}
      />
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Effective access</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {effective.length === 0 ? (
            <span className="text-sm text-muted-foreground">No permissions</span>
          ) : effective.map((permission) => (
            <span key={permission} className={cn(glassSubtle, 'px-2 py-1 font-mono text-[11px]')}>{permission}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdministrationPage = () => {
  const { can, user: currentUser } = useAuth();
  const canManageUsers = can('users.manage');
  const canViewCompanies = can('companies.view');
  const canCreateCompanies = can('*');
  const hasData = useRef(false);

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const [companyOpen, setCompanyOpen] = useState(false);
  const [company, setCompany] = useState(emptyCompany);
  const [companyAttempted, setCompanyAttempted] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);

  const [userOpen, setUserOpen] = useState(false);
  const [newUser, setNewUser] = useState(emptyUser);
  const [userAttempted, setUserAttempted] = useState(false);
  const [userSaving, setUserSaving] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [editAttempted, setEditAttempted] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const load = useCallback(async () => {
    const initial = !hasData.current;
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const [companyResult, userResult] = await Promise.allSettled([
        canViewCompanies ? companiesApi.getAll() : Promise.resolve({ data: [] }),
        usersApi.getAll(),
      ]);
      if (userResult.status === 'rejected') throw userResult.reason;
      setUsers(userResult.value.data || []);
      if (companyResult.status === 'fulfilled') setCompanies(companyResult.value.data || []);
      else {
        setCompanies([]);
        toast.error(apiMessage(companyResult.reason, 'Companies could not be loaded'));
      }
      hasData.current = true;
      setError('');
    } catch (loadError) {
      const message = apiMessage(loadError, 'Failed to load administration data');
      if (!hasData.current) setError(message);
      else toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canViewCompanies]);

  useEffect(() => {
    load();
  }, [load]);

  const companyErrors = companyAttempted ? validateCompany(company, companies) : {};
  const newUserErrors = userAttempted ? validateUser(newUser, { users }) : {};
  const editErrors = editUser && editAttempted
    ? validateUser(editUser, { editing: true, users, currentId: editUser.id })
    : {};

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((item) => {
      const companyName = companies.find((companyItem) => companyItem.id === item.company_id)?.name || '';
      return [item.name, item.email, item.role, roleLabel(item.role), companyName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [companies, query, users]);

  const companyName = (id) => companies.find((item) => item.id === id)?.name;

  const openEdit = (item) => {
    const grants = item.permission_grants || [];
    const revokes = item.permission_revokes || [];
    setEditAttempted(false);
    setEditUser({
      ...item,
      name: item.name || '',
      email: item.email || '',
      password: '',
      confirmPassword: '',
      company_id: item.company_id || '',
      role: normalizeRole(item.role),
      is_active: item.is_active !== false,
      ...reconcilePermissions(normalizeRole(item.role), grants, revokes),
    });
  };

  const changeRole = (current, role, setter) => {
    const next = reconcilePermissions(role, current.permission_grants, current.permission_revokes);
    setter({ ...current, role, ...next });
  };

  const createCompany = async (event) => {
    event.preventDefault();
    setCompanyAttempted(true);
    if (Object.keys(validateCompany(company, companies)).length) return;
    setCompanySaving(true);
    try {
      await companiesApi.create({
        name: company.name.trim(),
        code: company.code.trim().toUpperCase(),
        company_type: company.company_type,
      });
      toast.success('Company created');
      setCompanyOpen(false);
      setCompany(emptyCompany);
      setCompanyAttempted(false);
      await load();
    } catch (createError) {
      toast.error(apiMessage(createError, 'Failed to create company'));
    } finally {
      setCompanySaving(false);
    }
  };

  const createUser = async (event) => {
    event.preventDefault();
    setUserAttempted(true);
    if (Object.keys(validateUser(newUser, { users })).length) return;
    setUserSaving(true);
    try {
      const payload = {
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password,
        role: newUser.role,
        permission_grants: newUser.permission_grants,
        permission_revokes: newUser.permission_revokes,
      };
      if (newUser.company_id) payload.company_id = newUser.company_id;
      await usersApi.create(payload);
      toast.success('User created');
      setUserOpen(false);
      setNewUser(emptyUser);
      setUserAttempted(false);
      await load();
    } catch (createError) {
      toast.error(apiMessage(createError, 'Failed to create user'));
    } finally {
      setUserSaving(false);
    }
  };

  const persistUser = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const payload = {
        name: editUser.name.trim(),
        role: editUser.role,
        company_id: editUser.company_id || null,
        permission_grants: editUser.permission_grants,
        permission_revokes: editUser.permission_revokes,
        is_active: editUser.is_active,
      };
      if (editUser.password) payload.password = editUser.password;
      await usersApi.update(editUser.id, payload);
      toast.success('User updated');
      setConfirmDeactivate(false);
      setEditUser(null);
      setEditAttempted(false);
      await load();
    } catch (updateError) {
      toast.error(apiMessage(updateError, 'Failed to update user'));
    } finally {
      setEditSaving(false);
    }
  };

  const updateUser = async (event) => {
    event?.preventDefault();
    setEditAttempted(true);
    if (!editUser || Object.keys(validateUser(editUser, { editing: true, users, currentId: editUser.id })).length) return;
    const existing = users.find((item) => item.id === editUser.id);
    const wasActive = existing ? existing.is_active !== false : true;
    if (wasActive && !editUser.is_active) {
      setConfirmDeactivate(true);
      return;
    }
    await persistUser();
  };

  const companyOptions = (selectedId) => {
    const options = [...companies];
    if (selectedId && !options.some((item) => item.id === selectedId)) {
      options.unshift({ id: selectedId, name: 'Current company' });
    }
    return options;
  };

  const userForm = (draft, setDraft, errors, { editing = false } = {}) => (
    <div className="grid gap-4">
      <TextField
        id={editing ? 'edit-user-name' : 'user-name'}
        label="Name"
        required
        value={draft.name}
        error={errors.name}
        autoComplete="name"
        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
      />
      <TextField
        id={editing ? 'edit-user-email' : 'user-email'}
        label="Email"
        type="email"
        required
        value={draft.email}
        error={errors.email}
        autoComplete="email"
        disabled={editing}
        hint={editing ? 'Email cannot be changed here.' : undefined}
        onChange={(event) => setDraft({ ...draft, email: event.target.value })}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id={editing ? 'edit-user-password' : 'user-password'}
          label={editing ? 'New password' : 'Password'}
          type="password"
          required={!editing}
          value={draft.password}
          error={errors.password}
          autoComplete="new-password"
          hint={editing ? 'Leave blank to keep the current password.' : 'At least 8 characters.'}
          onChange={(event) => setDraft({ ...draft, password: event.target.value })}
        />
        <TextField
          id={editing ? 'edit-user-confirm' : 'user-confirm'}
          label="Confirm password"
          type="password"
          required={!editing || Boolean(draft.password)}
          value={draft.confirmPassword}
          error={errors.confirmPassword}
          autoComplete="new-password"
          onChange={(event) => setDraft({ ...draft, confirmPassword: event.target.value })}
        />
      </div>
      <Field id={editing ? 'edit-user-role' : 'user-role'} label="Role" required error={errors.role}>
        <Select value={draft.role} onValueChange={(role) => changeRole(draft, role, setDraft)}>
          <SelectTrigger
            id={editing ? 'edit-user-role' : 'user-role'}
            className={fieldControl}
            aria-invalid={errors.role ? true : undefined}
            aria-describedby={describedBy(editing ? 'edit-user-role' : 'user-role', errors.role)}
          >
            <SelectValue placeholder="Choose a role" />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((role) => (
              <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field
        id={editing ? 'edit-user-company' : 'user-company'}
        label="Company"
        hint="Partner roles should belong to a company. Vdrive roles can stay unassigned."
      >
        <Select
          value={draft.company_id || 'none'}
          onValueChange={(companyId) => setDraft({ ...draft, company_id: companyId === 'none' ? '' : companyId })}
        >
          <SelectTrigger id={editing ? 'edit-user-company' : 'user-company'} className={fieldControl}>
            <SelectValue placeholder="No company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No company</SelectItem>
            {companyOptions(draft.company_id).map((item) => (
              <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {editing ? (
        <Field id="edit-user-status" label="Status" required>
          <Select
            value={draft.is_active ? 'active' : 'inactive'}
            onValueChange={(value) => setDraft({ ...draft, is_active: value === 'active' })}
          >
            <SelectTrigger id="edit-user-status" className={fieldControl}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      ) : null}
      <PermissionEditor
        role={draft.role}
        grants={draft.permission_grants}
        revokes={draft.permission_revokes}
        error={errors.permissions}
        onChange={(next) => setDraft({ ...draft, ...next })}
      />
    </div>
  );

  return (
    <PageShell testId="administration-page">
      <PageHeader
        eyebrow="Access"
        title="Administration"
        description="Manage companies, users, roles, and permission overrides."
        actions={refreshing ? <span className="text-sm text-muted-foreground">Updating…</span> : null}
      />

      {loading ? <LoadingPanels testId="administration-loading" /> : null}
      {!loading && error ? <ErrorState message={error} onRetry={load} /> : null}

      {!loading && !error ? (
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList className={cn(glassSubtle, 'h-auto w-full justify-start gap-1 p-1 sm:w-auto')}>
            <TabsTrigger value="users" className="gap-2 rounded-xl data-[state=active]:bg-background/80 dark:data-[state=active]:bg-white/10">
              <Users className="h-4 w-4" aria-hidden="true" />
              Users
            </TabsTrigger>
            {canViewCompanies ? (
              <TabsTrigger value="companies" className="gap-2 rounded-xl data-[state=active]:bg-background/80 dark:data-[state=active]:bg-white/10">
                <Building2 className="h-4 w-4" aria-hidden="true" />
                Companies
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="users">
            <Card className={glassPanel}>
              <CardHeader className="gap-4 space-y-0 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" aria-hidden="true" />
                    Users and roles
                  </CardTitle>
                  <CardDescription>
                    {users.length} {users.length === 1 ? 'account' : 'accounts'}
                    {canManageUsers ? '' : '. You can view accounts. Creating or editing them requires users.manage.'}
                  </CardDescription>
                </div>
                {canManageUsers ? (
                  <Button type="button" className={pillButton} onClick={() => { setNewUser(emptyUser); setUserAttempted(false); setUserOpen(true); }}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add user
                  </Button>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-search">Search users</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <Input
                      id="user-search"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Name, email, role, or company"
                      className={cn(fieldControl, 'pl-10')}
                    />
                  </div>
                </div>
                {filteredUsers.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    testId="no-users"
                    title={users.length === 0 ? 'No users yet' : 'No users match this search'}
                    description={users.length === 0 ? 'Create an account to grant access to Vdrive.' : 'Try a different name, email, or role.'}
                    action={query ? (
                      <Button type="button" variant="outline" className={pillButton} onClick={() => setQuery('')}>
                        Clear search
                      </Button>
                    ) : null}
                  />
                ) : (
                  <>
                    <ScrollHint />
                    <Table className="min-w-[760px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((item) => {
                          const active = item.is_active !== false;
                          return (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell>{item.email}</TableCell>
                              <TableCell>{roleLabel(item.role)}</TableCell>
                              <TableCell>{companyName(item.company_id) || '—'}</TableCell>
                              <TableCell>
                                <span className={cn(
                                  'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
                                  active
                                    ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                    : 'bg-muted text-muted-foreground'
                                )}
                                >
                                  {active ? 'Active' : 'Inactive'}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                {canManageUsers ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className={pillButton}
                                    onClick={() => openEdit(item)}
                                    aria-label={`Edit ${item.name}`}
                                  >
                                    Edit
                                  </Button>
                                ) : (
                                  <span className="text-xs text-muted-foreground">View only</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {canViewCompanies ? (
            <TabsContent value="companies">
              <Card className={glassPanel}>
                <CardHeader className="gap-4 space-y-0 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <CardTitle>Companies</CardTitle>
                    <CardDescription>
                      {canCreateCompanies ? 'Partner companies that can own chargers and users.' : 'You can view companies. Creating them requires super admin access.'}
                    </CardDescription>
                  </div>
                  {canCreateCompanies ? (
                    <Button type="button" className={pillButton} onClick={() => { setCompany(emptyCompany); setCompanyAttempted(false); setCompanyOpen(true); }}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Add company
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent>
                  {companies.length === 0 ? (
                    <EmptyState
                      icon={Building2}
                      testId="no-companies"
                      title="No companies yet"
                      description="Add a partner company before assigning users to it."
                    />
                  ) : (
                    <>
                      <ScrollHint />
                      <Table className="min-w-[560px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Code</TableHead>
                            <TableHead>Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {companies.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell className="font-mono">{item.code}</TableCell>
                              <TableCell className="capitalize">{item.company_type || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ) : null}
        </Tabs>
      ) : null}

      <Dialog open={companyOpen} onOpenChange={(open) => { if (!companySaving) { setCompanyOpen(open); if (!open) setCompanyAttempted(false); } }}>
        <DialogContent className={cn(glassStrong, 'max-h-[90vh] overflow-y-auto')}>
          <DialogHeader>
            <DialogTitle>New company</DialogTitle>
            <DialogDescription>Companies group partner users and the chargers they can access.</DialogDescription>
          </DialogHeader>
          <form className="grid gap-4" onSubmit={createCompany} noValidate>
            <TextField id="company-name" label="Name" required value={company.name} error={companyErrors.name} onChange={(event) => setCompany({ ...company, name: event.target.value })} />
            <TextField
              id="company-code"
              label="Code"
              required
              value={company.code}
              error={companyErrors.code}
              hint="Short identifier, such as INSPECT."
              onChange={(event) => setCompany({ ...company, code: event.target.value.toUpperCase() })}
            />
            <Field id="company-type" label="Type" required error={companyErrors.company_type}>
              <Select value={company.company_type} onValueChange={(company_type) => setCompany({ ...company, company_type })}>
                <SelectTrigger id="company-type" className={fieldControl} aria-invalid={companyErrors.company_type ? true : undefined}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" className={pillButton} disabled={companySaving} onClick={() => setCompanyOpen(false)}>Cancel</Button>
              <Button type="submit" className={pillButton} disabled={companySaving}>{companySaving ? 'Creating…' : 'Create company'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={userOpen} onOpenChange={(open) => { if (!userSaving) { setUserOpen(open); if (!open) setUserAttempted(false); } }}>
        <DialogContent className={cn(glassStrong, 'max-h-[90vh] overflow-y-auto sm:max-w-2xl')}>
          <DialogHeader>
            <DialogTitle>New user</DialogTitle>
            <DialogDescription>Choose a role, then grant or revoke individual permissions.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} noValidate>
            {userForm(newUser, setNewUser, newUserErrors)}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" className={pillButton} disabled={userSaving} onClick={() => setUserOpen(false)}>Cancel</Button>
              <Button type="submit" className={pillButton} disabled={userSaving}>{userSaving ? 'Creating…' : 'Create user'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editUser)} onOpenChange={(open) => { if (!open && (editSaving || confirmDeactivate)) return; if (!open) { setEditUser(null); setEditAttempted(false); } }}>
        <DialogContent className={cn(glassStrong, 'max-h-[90vh] overflow-y-auto sm:max-w-2xl')}>
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>
              {editUser?.id === currentUser?.id
                ? 'This is your account. Deactivating it will sign you out on the next request.'
                : 'Update the role, company, password, or permission overrides.'}
            </DialogDescription>
          </DialogHeader>
          {editUser ? (
            <form onSubmit={updateUser} noValidate>
              {userForm(editUser, setEditUser, editErrors, { editing: true })}
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" className={pillButton} disabled={editSaving} onClick={() => setEditUser(null)}>Cancel</Button>
                <Button type="submit" className={pillButton} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save changes'}</Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeactivate}
        onOpenChange={setConfirmDeactivate}
        title={`Deactivate ${editUser?.name || 'this user'}?`}
        description="Inactive users cannot sign in. You can activate the account again later."
        confirmLabel="Deactivate user"
        destructive
        pending={editSaving}
        onConfirm={persistUser}
      />
    </PageShell>
  );
};

export default AdministrationPage;
